import { usePage } from "@inertiajs/react";
import ContributionsList from "components/authors/ContributionsList";
import CoverImage from "components/Cover/CoverImage";
import BookMatchPercentage from "components/match/BookMatchPercentage/BookMatchPercentage";
import { BookSeriesTag } from "components/series/BookSeriesTag";
import { BookButton } from "features/BookButton";
import Container from "hardcover-ui/components/Container";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import { secondsFormat } from "lib/bookUtils";
import classNames from "lib/classNames";
import { dateAsWords } from "lib/dateFormat";
import { findHeaderForGenres, headerPath } from "lib/genres/findHeader";
import { enlargeUrl, normalizeImageUrl } from "lib/imageTools";
import pluralize from "lib/pluralize";
import { BookPageProps } from "pages/clientverse/books/show.props";
import { BookSerializersBookByline, PageSerializersBooksLayout } from "types/serializers";
import BookRatingDetails from "./BookRatingDetails";

const defaultHeader = `${headerPath}/bookstore.webp`;
function findBookCover(book: PageSerializersBooksLayout) {
  if (book.headerImage?.url) {
    const basePath = normalizeImageUrl(book.headerImage.url);
    return enlargeUrl({ url: basePath, width: 2448, height: 496 });
  }

  return enlargeUrl({url: findHeaderForGenres(book.genres, defaultHeader), width: 2448, height: 496 });
}

function calculateHeaderSize(length: number) {
  let smBookHeaderSize = "text-3xl";
  let lgBookHeaderSize = "lg:text-5xl";

  if (length > 200) {
    lgBookHeaderSize = "text-sm";
    smBookHeaderSize = "lg:text-md";
  }
  if (length > 100) {
    lgBookHeaderSize = "text-md";
    smBookHeaderSize = "lg:text-lg";
  }
  if (length >= 50) {
    lgBookHeaderSize = "text-lg";
    smBookHeaderSize = "lg:text-2xl";
  }
  if (length >= 20) {
    smBookHeaderSize = "text-2xl";
    lgBookHeaderSize = "lg:text-4xl";
  }

  return {
    smBookHeaderSize,
    lgBookHeaderSize,
  };
}

function parseTitle(title: string) {
  if (title.length < 60 || title.indexOf(":") === -1) {
    return { title, subtitle: null };
  }
  // This is a long title. Split it into title and subtitle
  const titleParts = title.split(":");

  return {
    title: titleParts[0],
    subtitle: title.replace(`${titleParts[0]}:`, "").trim(),
  };
}

export default function BookHeader() {
  const { book } = usePage<BookPageProps>().props;
  const coverImageUrl = findBookCover(book);
  const { title, subtitle } = parseTitle(book.title);
  const { isReleased } = book;
  
  const pageInfo = [];
  if (book.releaseYear) {
    if (isReleased) {
      pageInfo.push(book.releaseYear);
    } else if (book.releaseDate) {
      pageInfo.push(`Available on ${dateAsWords(book.releaseDate)}`);
    } else {
      pageInfo.push(`Available in ${book.releaseYear}`);
    }
  }
  if (book.pages > 0) {
    pageInfo.push(
      `${book.pages.toLocaleString()} ${pluralize("page", book.pages)}`
    );
  }
  if (book.audioSeconds > 0) {
    pageInfo.push(secondsFormat(book.audioSeconds));
  }
  const pageData = pageInfo.join(" • ");

  
  const { smBookHeaderSize, lgBookHeaderSize } = calculateHeaderSize(
    title.length
  );

  return (
    <div
      className="relative"
      style={{
        marginTop: "-64px",
        paddingTop: "64px",
      }}
    >
      <div
        className="absolute top-0 bg-repeat bg-top w-full lg:h-[300px] fade-bottom-lg"
        style={{
          backgroundImage: `url('${coverImageUrl}')`,
          height: "233px",
        }}
      >
        <div
          className={classNames(
            "h-full flex flex-col justify-end items-center",
          
          )}
         />
      </div>
      <div className="relative mt-16 md:mt-32 lg:mt-36 ">
        <Container variant="layout"  size="md">
          <div className="flex flex-row-reverse md:flex-row gap-4 w-full">
            <div className="flex-none">
              <div className="hidden md:block shadow-xshover:shadow-2xl transition-all " >
                <CoverImage
                  id={book.id}
                  image={book.image}
                  size="lg"
                  title={book.title}
                  variant="secondary"
                  settings={{
                    stretch: "x",
                  }}
                />
              </div>

              <div className="md:hidden shadow-xs hover:shadow-2xl transition-all ">
                <CoverImage
                  id={book.id}
                  image={book.image}
                  size="md"
                  title={book.title}
                  variant="secondary"
                  settings={{
                    stretch: "x",
                  }}
                />
              </div>

              <div className="lg:hidden mt-8 flex flex-row justify-center">
                <BookButton bookId={book.id} size="sm" />
              </div>
            </div>

            <div className="grow flex flex-col justify-between">
              <div>
                <div className="flex-row space-x-2 flex">
                  <BookSeriesTag book={book as BookSerializersBookByline} link />
                  <span className="flex items-start">
                    <BookMatchPercentage bookId={book.id} />
                  </span>
                </div>
                <div className="mt-4">
                  <Heading
                    level={1}
                    variant="primary"
                    size="none"
                    className={`mb-1 ${smBookHeaderSize} ${lgBookHeaderSize}`}
                  >
                    {title}
                  </Heading>
                  <div className="mt-2 lg:mt-0">
                    {subtitle && (
                      <Text
                        variant="secondary"
                        size="lg"
                        as="p"
                        className="leading-6 font-semibold mb-1 lg:mb-2"
                      >
                        {subtitle}
                      </Text>
                    )}
                    <ContributionsList
                      className="font-semibold text-sm hidden lg:flex"
                      contributions={book.contributions}
                      size="xs"
                      avatar={book.contributions.length <= 2}
                      lazy={false}
                      separator=","
                      expand
                      byLine
                    />
                  </div>
                </div>
              </div>

              <div className="md:grid md:grid-cols-4 gap-4 lg:grid-rows-1 lg:mt-2">
                <div className="flex flex-col col-span-4 lg:col-span-2">
                  <div className="hidden lg:block">
                    <BookButton bookId={book.id} />
                  </div>

                  <ContributionsList
                    className="font-semibold text-sm lg:hidden mt-1"
                    contributions={book.contributions}
                    size="xs"
                    avatar={book.contributions.length <= 2}
                    lazy={false}
                    separator=","
                    byLine
                  />
                  <div className="mt-3 mb-2 flex justify-between">
                    <Text
                      variant="secondary"
                      size="sm"
                      className="font-semibold"
                    >
                      {pageData}
                    </Text>
                  </div>
                </div>
                <BookRatingDetails book={book} />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
